import React, { useEffect, useMemo, useState } from 'react';
import { BIBLE_LANGUAGES } from '../../constants';
import { createPace, getTodayDateString, isValidDueDate } from '../../utils/deck_pace_utils';
import {
  dedupeVerseTuples,
  verseTupleToKey,
} from '../../utils/canonical_verse_utils';
import { fetchLSMVersesWithRetry } from '../../utils/lsm_api';
import { getBibleLanguageFromCookie } from '../../utils/theme_utils';
import {
  formatVerseTuple,
  parseVerseReferences,
} from '../../utils/verse_reference_parser';
import { Icon } from '../Icon';
import { VersePreviewList } from './VersePreviewList';

const fieldStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '11px 12px',
  border: '1px solid var(--md-sys-color-outline)',
  borderRadius: '10px',
  backgroundColor: 'var(--md-sys-color-surface)',
  color: 'var(--md-sys-color-on-surface)',
  font: 'inherit',
};

const sectionStyle = {
  display: 'block',
  padding: '18px',
  marginBottom: '16px',
};

const VERSE_EDITOR_PAGE_SIZE = 100;
const VERSE_EDITOR_PREVIEW_SIZE = 3;

function normalizePreview(response, tuples) {
  const fetchedVerses = Array.isArray(response) ? response : response?.verses;
  if (!Array.isArray(fetchedVerses)) {
    return [];
  }
  return tuples.map((tuple, index) => {
    const fetchedVerse = fetchedVerses[index];
    return {
      ref: typeof fetchedVerse?.ref === 'string'
        ? fetchedVerse.ref
        : formatVerseTuple(tuple),
      text: typeof fetchedVerse === 'string'
        ? fetchedVerse
        : fetchedVerse?.text,
    };
  }).filter(verse => typeof verse.text === 'string' && verse.text.trim());
}

export function DeckSettingsTab({
  deck,
  user,
  membership,
  members,
  busy,
  onSignIn,
  onJoin,
  onLeave,
  onArchive,
  onRename,
  onVisibility,
  onPace,
  onAdminRole,
  onRemoveMember,
  onRewriteVerses,
  showToast,
}) {
  const [name, setName] = useState(
    typeof deck.deck_name === 'string' ? deck.deck_name : ''
  );
  const [isPublic, setIsPublic] = useState(deck.is_public);
  const [dueDate, setDueDate] = useState(deck.due_date || '');
  const [paceTarget, setPaceTarget] = useState(deck.pace?.verses_per_day || 1);
  const [referenceInput, setReferenceInput] = useState('');
  const [verses, setVerses] = useState(Array.isArray(deck.verses) ? deck.verses : []);
  const [versePage, setVersePage] = useState(0);
  const [editorError, setEditorError] = useState('');
  const [preview, setPreview] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const isAdmin = membership?.role === 'admin';
  const isDeckCreator = Boolean(user && deck.created_by === user.uid);
  const versePageCount = Math.max(1, Math.ceil(verses.length / VERSE_EDITOR_PAGE_SIZE));
  const visibleVerses = useMemo(
    () => verses.slice(
      versePage * VERSE_EDITOR_PAGE_SIZE,
      (versePage + 1) * VERSE_EDITOR_PAGE_SIZE
    ),
    [versePage, verses]
  );

  useEffect(() => {
    setName(typeof deck.deck_name === 'string' ? deck.deck_name : '');
    setIsPublic(deck.is_public);
    setDueDate(deck.due_date || '');
    setPaceTarget(deck.pace?.verses_per_day || 1);
    setVerses(Array.isArray(deck.verses) ? deck.verses : []);
    setVersePage(0);
  }, [deck]);

  useEffect(() => {
    if (versePage >= versePageCount) {
      setVersePage(versePageCount - 1);
    }
  }, [versePage, versePageCount]);

  useEffect(() => {
    if (!isAdmin || visibleVerses.length === 0) {
      setPreview([]);
      setPreviewError('');
      return undefined;
    }
    let active = true;
    const tuples = visibleVerses.slice(0, VERSE_EDITOR_PREVIEW_SIZE);
    const references = tuples.map(formatVerseTuple).join('; ');
    const language = getBibleLanguageFromCookie() || BIBLE_LANGUAGES.ENGLISH;
    setPreviewLoading(true);
    setPreviewError('');
    fetchLSMVersesWithRetry(references, 'default', language)
      .then(response => {
        if (active) {
          setPreview(normalizePreview(response, tuples));
        }
      })
      .catch(() => {
        if (active) {
          setPreview([]);
          setPreviewError('Bible text preview is temporarily unavailable.');
        }
      })
      .finally(() => {
        if (active) {
          setPreviewLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [isAdmin, visibleVerses]);

  const deckUrl = `${window.location.origin}/deck?id=${deck.id}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(deckUrl);
      showToast('Deck link copied.');
    } catch (error) {
      showToast('Unable to copy the deck link.');
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: name || 'Memorization Deck', url: deckUrl });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') {
          return;
        }
      }
    }
    await copyLink();
  };

  const addVerses = event => {
    event.preventDefault();
    setEditorError('');
    try {
      const nextVerses = dedupeVerseTuples([
        ...verses,
        ...parseVerseReferences(referenceInput),
      ]);
      setVerses(nextVerses);
      setReferenceInput('');
      showToast(`${nextVerses.length - verses.length} new verses added.`);
    } catch (error) {
      setEditorError(error.message);
    }
  };

  const saveVerses = () => {
    if (verses.length === 0) {
      setEditorError('A deck must contain at least one verse.');
      return;
    }
    if (!window.confirm(`Replace this deck with ${verses.length} verses? Member progress may need review.`)) {
      return;
    }
    onRewriteVerses(verses);
  };

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: '16px' }}>
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>
        <h2 style={{ marginTop: 0 }}>Deck Settings</h2>
        <md-elevated-card style={sectionStyle}>
          <h3 style={{ marginTop: 0 }}>Sharing</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            <md-filled-button onClick={share}>
              <Icon name="shareDeck" slot="icon" />
              Share
            </md-filled-button>
            <md-outlined-button onClick={copyLink}>
              <Icon name="copyDeck" slot="icon" />
              Copy Link
            </md-outlined-button>
          </div>
        </md-elevated-card>

        <md-elevated-card style={sectionStyle}>
          <h3 style={{ marginTop: 0 }}>Membership</h3>
          {!user ? (
            <md-filled-button onClick={onSignIn}>Sign In to Join</md-filled-button>
          ) : membership ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              <md-outlined-button
                {...(busy ? { disabled: true } : {})}
                onClick={onArchive}
              >
                <Icon name="archiveDeck" slot="icon" />
                Archive
              </md-outlined-button>
              <md-outlined-button
                {...((busy || isDeckCreator) ? { disabled: true } : {})}
                onClick={() => {
                  if (window.confirm('Leave this deck and remove your membership?')) {
                    onLeave();
                  }
                }}
              >
                <Icon name="leaveDeck" slot="icon" />
                Leave Deck
              </md-outlined-button>
              <p style={{ width: '100%', color: 'var(--md-sys-color-on-surface-variant)' }}>
                {isDeckCreator
                  ? 'You created this deck, so you always keep admin access. Archive it to hide it from your home page; you can restore it from Archived Decks.'
                  : 'Archiving hides this deck from your home page. Restore it any time from Archived Decks.'}
              </p>
            </div>
          ) : (
            <md-filled-button {...(busy ? { disabled: true } : {})} onClick={onJoin}>
              <Icon name="joinDeck" slot="icon" />
              Join Deck
            </md-filled-button>
          )}
        </md-elevated-card>

        {isAdmin && (
          <>
            <md-elevated-card style={sectionStyle}>
              <h3 style={{ marginTop: 0 }}>Deck Details</h3>
              <label htmlFor="deck-settings-name">Name</label>
              <div style={{ display: 'flex', gap: '8px', margin: '8px 0 18px' }}>
                <input
                  id="deck-settings-name"
                  value={name}
                  onChange={event => setName(event.target.value)}
                  style={fieldStyle}
                />
                <md-filled-button
                  {...((busy || !name.trim()) ? { disabled: true } : {})}
                  onClick={() => onRename(name)}
                >
                  Save
                </md-filled-button>
              </div>
              <label style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '18px' }}>
                <input
                  type="checkbox"
                  {...(isPublic ? { checked: true } : {})}
                  onChange={event => {
                    setIsPublic(event.target.checked);
                    onVisibility(event.target.checked);
                  }}
                />
                Publicly discoverable
              </label>
              <p style={{
                margin: '-8px 0 18px',
                color: 'var(--md-sys-color-on-surface-variant)',
              }}>
                Both private and public decks can be shared by link. Public decks are also listed in discovery.
              </p>
              <label htmlFor="deck-settings-pace">Verses per day</label>
              <input
                id="deck-settings-pace"
                type="number"
                min="1"
                value={paceTarget}
                onChange={event => setPaceTarget(Math.max(1, Number(event.target.value)))}
                style={{ ...fieldStyle, margin: '8px 0 12px' }}
              />
              <label htmlFor="deck-settings-due">Due date</label>
              <input
                id="deck-settings-due"
                type="date"
                min={getTodayDateString()}
                value={dueDate}
                onChange={event => setDueDate(event.target.value)}
                style={{ ...fieldStyle, margin: '8px 0 12px' }}
              />
              <md-filled-button
                {...((busy || !isValidDueDate(dueDate, getTodayDateString())) ? { disabled: true } : {})}
                onClick={() => onPace(createPace('custom', paceTarget), dueDate || null)}
              >
                Save Pace
              </md-filled-button>
            </md-elevated-card>

            <md-elevated-card style={sectionStyle}>
              <h3 style={{ marginTop: 0 }}>Members</h3>
              {members.map(member => {
                const memberIsAdmin = member.role === 'admin';
                const memberIsCreator = member.id === deck.created_by;
                return (
                  <div
                    key={member.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 0',
                      borderBottom: '1px solid var(--md-sys-color-outline-variant)',
                    }}
                  >
                    <div>
                      <strong>{member.displayName}{member.id === user.uid ? ' (you)' : ''}</strong>
                      <div style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
                        {memberIsCreator ? 'Creator · Admin' : memberIsAdmin ? 'Admin' : 'Member'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <md-outlined-button
                        {...((busy || memberIsCreator) ? { disabled: true } : {})}
                        onClick={() => onAdminRole(member, !memberIsAdmin)}
                      >
                        {memberIsAdmin ? 'Revoke Admin' : 'Grant Admin'}
                      </md-outlined-button>
                      {member.id !== user.uid && (
                        <md-outlined-button
                          {...((busy || memberIsCreator) ? { disabled: true } : {})}
                          onClick={() => {
                            if (window.confirm(`Remove ${member.displayName} from this deck?`)) {
                              onRemoveMember(member);
                            }
                          }}
                        >
                          Remove
                        </md-outlined-button>
                      )}
                    </div>
                  </div>
                );
              })}
            </md-elevated-card>

            <md-elevated-card style={sectionStyle}>
              <h3 style={{ marginTop: 0 }}>Verse Editor</h3>
              <form onSubmit={addVerses} style={{ display: 'flex', gap: '8px' }}>
                <input
                  aria-label="Add verse references"
                  value={referenceInput}
                  onChange={event => setReferenceInput(event.target.value)}
                  placeholder="John 3:16; Gal 4:1-3"
                  style={fieldStyle}
                />
                <md-filled-button
                  type="submit"
                  {...(!referenceInput.trim() ? { disabled: true } : {})}
                >
                  Add
                </md-filled-button>
              </form>
              {editorError && <p role="alert" style={{ color: 'var(--md-sys-color-error)' }}>{editorError}</p>}
              <p>{verses.length.toLocaleString()} verses</p>
              <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                {visibleVerses.map(tuple => (
                  <div
                    key={verseTupleToKey(tuple)}
                    style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0' }}
                  >
                    <span>{formatVerseTuple(tuple)}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${formatVerseTuple(tuple)}`}
                      onClick={() => setVerses(current => current.filter(
                        item => verseTupleToKey(item) !== verseTupleToKey(tuple)
                      ))}
                      style={{ border: 0, background: 'transparent', color: 'var(--md-sys-color-error)' }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              {versePageCount > 1 && (
                <nav
                  aria-label="Verse editor pages"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    margin: '12px 0',
                  }}
                >
                  <button
                    type="button"
                    {...(versePage === 0 ? { disabled: true } : {})}
                    onClick={() => setVersePage(current => current - 1)}
                  >
                    Previous
                  </button>
                  <span>Page {versePage + 1} of {versePageCount}</span>
                  <button
                    type="button"
                    {...(versePage === versePageCount - 1 ? { disabled: true } : {})}
                    onClick={() => setVersePage(current => current + 1)}
                  >
                    Next
                  </button>
                </nav>
              )}
              <section aria-label="Verse text preview">
                <h4>Text Preview</h4>
                {previewLoading && <p role="status">Loading Bible text…</p>}
                {previewError && <p role="alert">{previewError}</p>}
                {!previewLoading && (
                  <VersePreviewList verses={preview} />
                )}
              </section>
              <md-filled-button
                {...((busy || verses.length === 0) ? { disabled: true } : {})}
                onClick={saveVerses}
              >
                <Icon name="saveDeck" slot="icon" />
                Save Verses
              </md-filled-button>
            </md-elevated-card>
          </>
        )}
      </div>
    </div>
  );
}
